import React from "react";
import PropTypes from "prop-types";

/**
 * @param {string} raw
 * @returns {"high"|"mid"|"low"|null}
 */
export function normalizeLeadScoreTier(raw) {
    const s = String(raw || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    if (!s) {
        return null;
    }
    // Backward-compatible aliases from older API values.
    if (s === "high" || s === "core") {
        return "high";
    }
    if (s === "mid" || s === "medium" || s === "moderate") {
        return "mid";
    }
    if (s === "low" || s === "peripheral" || s === "periferal" || s === "perifetal") {
        return "low";
    }
    return null;
}

const CANONICAL_SCORE_LABELS = {
    high: "High",
    mid: "Moderate",
    low: "Low"
};

/**
 * @param {string} [score]
 * @param {{ displayCanonical?: boolean }} [options]
 * @returns {{ tier: string, className: string, label: string }}
 */
export function getLeadScoreBadgeProps(score, options = {}) {
    const raw = String(score || "").trim();
    if (!raw) {
        return {
            tier: "unknown",
            className: "lead-score-badge lead-score-badge--unknown",
            label: "Unavailable"
        };
    }
    const tier = normalizeLeadScoreTier(raw);
    const tierKey = tier || "unknown";
    const label =
        options.displayCanonical && tier && CANONICAL_SCORE_LABELS[tier]
            ? CANONICAL_SCORE_LABELS[tier]
            : raw;
    return {
        tier: tierKey,
        className: `lead-score-badge lead-score-badge--${tierKey}`,
        label
    };
}

export function LeadScoreBadge({ score, className = "", size = "md", displayCanonical = false }) {
    const { className: tierClass, label } = getLeadScoreBadgeProps(score, { displayCanonical });
    const sizeClass =
        size === "lg" ? "lead-score-badge--lg" : size === "sm" ? "lead-score-badge--sm" : "";
    const title = label || undefined;
    return (
        <span
            className={[tierClass, sizeClass, className].filter(Boolean).join(" ")}
            title={title}
            aria-label={label ? `Lead score: ${label}` : "Lead score unavailable"}
        >
            {label}
        </span>
    );
}

LeadScoreBadge.propTypes = {
    score: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    displayCanonical: PropTypes.bool
};

function toStartCase(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function normalizeMemberStatusKey(status) {
    return String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, " ")
        .replace(/[.]+/g, "")
        .trim();
}

function formatMemberStatusLabel(status) {
    const raw = String(status || "").trim();
    if (!raw) {
        return "Unavailable";
    }
    const normalized = normalizeMemberStatusKey(raw);
    if (normalized === "pending authorization" || normalized === "pending authorisation") {
        return "Pending Auth";
    }
    return toStartCase(raw);
}

/**
 * Member status badge: Active → low (green), anything else → mid (orange).
 * @param {string} [status]
 * @returns {{ tier: string, className: string, label: string }}
 */
export function getMemberStatusBadgeProps(status) {
    const raw = String(status || "").trim();
    if (!raw) {
        return {
            tier: "unknown",
            className:
                "lead-score-badge lead-member-status-badge lead-score-badge--unknown",
            label: "Unavailable"
        };
    }
    const isActive = normalizeMemberStatusKey(raw) === "active";
    const tier = isActive ? "low" : "mid";
    return {
        tier,
        className: `lead-score-badge lead-member-status-badge lead-score-badge--${tier}`,
        label: formatMemberStatusLabel(raw)
    };
}

export function MemberStatusBadge({ status, className = "" }) {
    const { className: tierClass, label } = getMemberStatusBadgeProps(status);
    const raw = String(status || "").trim();
    const normalized = normalizeMemberStatusKey(raw);
    const title =
        normalized === "pending authorization" || normalized === "pending authorisation"
            ? toStartCase(raw)
            : label || undefined;
    return (
        <span
            className={[tierClass, className].filter(Boolean).join(" ")}
            title={title}
            aria-label={label ? `Member status: ${label}` : "Member status unavailable"}
        >
            {label}
        </span>
    );
}

MemberStatusBadge.propTypes = {
    status: PropTypes.string,
    className: PropTypes.string
};
